/**
 * 合同审查庭 - 核心状态机 Reducer
 * v3: 卡包商店替代访谈 + Balatro 扑克质证
 */

import type {
  ContractGameState,
  ContractGameAction,
  ClauseLine,
  Tracks,
  TracksDelta,
  TrackId,
  ClauseOutcome,
  GameConfig,
  Stakeholder,
  PackId,
  PlayingCard,
  ClauseThreshold,
  Joker,
  BlindConfig,
  BossEffect,
  BlindType,
} from './types'
import clauseLinesData from '../data/clauseLines.json'
import stakeholdersData from '../data/stakeholders.json'
import { DEFAULT_CONFIG, TRACK_CONFIGS } from '../data/gameConfig'
import { CONTRACT_DOCUMENT } from '../data/contractDocument'
import {
  CLAUSE_THRESHOLDS,
  CLAUSE_POINTS,
  POKER_CONFIG,
  JOKER_PRICE,
  ALL_JOKERS,
  ALL_PACKS,
  getInitialHandTypeLevels,
  BLIND_CLAUSE_CONFIG,
  BLIND_LABELS,
  BOSS_EFFECTS,
  BLIND_REWARDS,
  MAX_INTEREST,
  ALL_SUITS,
} from '../data/pokerConfig'
import {
  buildDeck,
  shuffleDeck,
  evaluatePokerHand,
  scoreHandDetailed,
  checkMilestones,
  upgradeRank,
} from './pokerEvaluator'

// =============================================================================
// Data loading
// =============================================================================

const ALL_CLAUSES: ClauseLine[] = clauseLinesData as ClauseLine[]
const ALL_STAKEHOLDERS: Stakeholder[] = stakeholdersData as Stakeholder[]

// =============================================================================
// Helpers
// =============================================================================

function clampTrack(value: number, trackId: TrackId): number {
  const config = TRACK_CONFIGS.find(t => t.id === trackId)!
  return Math.max(config.min, Math.min(config.max, value))
}

function applyDelta(tracks: Tracks, delta: TracksDelta): Tracks {
  const result = { ...tracks }
  for (const key of Object.keys(delta) as TrackId[]) {
    const d = delta[key]
    if (d !== undefined) {
      result[key] = clampTrack(result[key] + d, key)
    }
  }
  return result
}

function buildClausePool(_config: GameConfig): ClauseLine[] {
  const boss = ALL_CLAUSES.find(c => c.isBoss)!
  const normals = ALL_CLAUSES.filter(c => !c.isBoss)
  return [...normals, boss]
}

function isDeliverable(tracks: Tracks): boolean {
  const thresholds = DEFAULT_CONFIG.deliverableThresholds
  return (
    tracks.D >= thresholds.D &&
    tracks.S <= thresholds.S &&
    tracks.L <= thresholds.L &&
    tracks.B >= thresholds.B
  )
}

/** 获取商店 Joker 展示池（含 fallback + 排除已装备 + 洗牌 + 切片） */
function getShopJokerPool(state: ContractGameState, extraExcludeIds: string[] = []): Joker[] {
  let pool = resolveJokerUnlocks(state)
  if (pool.length === 0) {
    pool = ALL_JOKERS.map(j => ({ ...j, unlocked: true }))
  }
  const excludeIds = new Set([
    ...state.equippedJokers.map(e => e.id),
    ...extraExcludeIds,
  ])
  const available = pool.filter(j => !excludeIds.has(j.id))
  return shuffleDeck(available).slice(0, POKER_CONFIG.JOKER_SHOP_SLOTS)
}

// =============================================================================
// Score computation (Balatro version)
// =============================================================================

function computeScore(state: ContractGameState): number {
  let score = 0

  // Points per reversed clause
  for (const outcome of state.clauseOutcomes) {
    if (outcome.result !== 'failure') {
      score += CLAUSE_POINTS[outcome.clauseId] ?? 0
    }
  }

  // Victory bonus
  if (state.victory) score += 15

  // Pack bonus (encourage buying 2 packs)
  score += Math.min(5, state.purchasedPacks.length * 2)

  return Math.max(0, Math.min(100, score))
}

// =============================================================================
// Initial state
// =============================================================================

export function createInitialState(config: GameConfig = DEFAULT_CONFIG): ContractGameState {
  const clausePool = buildClausePool(config)

  const initialTracks: Tracks = { D: 0, S: 0, L: 0, B: 0 }
  for (const tc of config.tracks) {
    initialTracks[tc.id] = tc.initial
  }

  return {
    phase: 'IDLE',
    tracks: initialTracks,
    clausePool,

    // Discovery
    discoveredClauseIds: [],
    lastDiscovery: null,
    lastDecoyFeedback: null,

    // Pack shop
    purchasedPacks: [],
    consumables: [],
    collectedCards: [],
    shopMoney: POKER_CONFIG.SHOP_MONEY,

    // Challenge — Balatro poker
    challengeSubPhase: null,

    // Blind system
    currentBlindIndex: 0,
    blinds: [],
    currentBlind: null,
    bossEffect: null,
    isInterShop: false,
    usedHandTypes: [],
    lockedHandType: null,

    // Poker deck state
    deck: [],
    hand: [],
    discardPile: [],
    selectedCardIds: [],
    playedCards: [],
    drawnCardIds: [],

    // Hands/discards resources
    handsRemaining: POKER_CONFIG.INITIAL_HANDS,
    discardsRemaining: POKER_CONFIG.INITIAL_DISCARDS,

    // Resource caps (can be modified by buffs)
    maxJokers: POKER_CONFIG.MAX_JOKERS,
    maxConsumables: POKER_CONFIG.MAX_CONSUMABLES,
    maxPlayCards: POKER_CONFIG.MAX_PLAY_CARDS,
    handSize: POKER_CONFIG.HAND_SIZE,

    // Scoring
    runningScore: 0,
    lastHandResult: null,

    // Clause thresholds
    clauseThresholds: [],
    allClauseThresholds: [],
    reversedClauseIds: [],
    newlyReversedClauseId: null,

    // Joker system
    jokerPool: [],
    equippedJokers: [],

    // Consumables (challenge phase)
    consumableHand: [],

    // Hand type levels
    handTypeLevels: getInitialHandTypeLevels(),

    // Records
    clauseOutcomes: [],
    contractTags: [],
    gameOver: false,
    victory: false,
    log: [],
  }
}

// =============================================================================
// Main Reducer
// =============================================================================

export function contractReducer(
  state: ContractGameState,
  action: ContractGameAction
): ContractGameState {
  switch (action.type) {
    // Discovery
    case 'START_GAME':
      return handleStartGame()
    case 'DISCOVER_KEYWORD':
      return handleDiscoverKeyword(state, action.clauseId)
    case 'CLICK_DECOY':
      return handleClickDecoy(state, action.decoyId)
    case 'DISMISS_DISCOVERY':
      return handleDismissDiscovery(state)

    // Pack shop
    case 'START_SHOP':
      return handleStartShop(state)
    case 'SELECT_FROM_PACK':
      return handleSelectFromPack(state, action.packId, action.consumableId)
    case 'BUY_JOKER':
      return handleBuyJoker(state, action.jokerId)
    case 'REROLL_JOKERS':
      return handleRerollJokers(state)

    // Challenge — Balatro poker
    case 'START_CHALLENGE':
      return handleStartChallenge(state)
    case 'SELECT_HAND_CARD':
      return handleSelectHandCard(state, action.cardId)
    case 'DESELECT_HAND_CARD':
      return handleDeselectHandCard(state, action.cardId)
    case 'PLAY_HAND':
      return handlePlayHand(state)
    case 'DISCARD_SELECTED':
      return handleDiscardSelected(state)
    case 'USE_CONSUMABLE':
      return handleUseConsumable(state, action.consumableId, action.targetCardIds)
    case 'FINISH_SCORING':
      return handleFinishScoring(state)
    case 'ADVANCE_PRESENTATION':
      return handleAdvancePresentation(state)

    // Blind system
    case 'START_BLIND':
      return handleStartBlind(state)
    case 'CONTINUE_TO_SHOP':
      return handleContinueToShop(state)
    case 'CONTINUE_TO_NEXT_BLIND':
      return handleContinueToNextBlind(state)

    // Utility
    case 'DEBUG_SKIP_PHASE':
      return handleDebugSkipPhase(state)
    case 'DEBUG_SKIP_STEP':
      return handleDebugSkipStep(state)
    case 'RESTART':
      return createInitialState()

    default:
      return state
  }
}

// =============================================================================
// Action Handlers — Discovery
// =============================================================================

function handleStartGame(): ContractGameState {
  const state = createInitialState()
  return {
    ...state,
    phase: 'CONTRACT_DISCOVERY',
    log: [{ action: 'START_GAME', result: '游戏开始，进入合同审查阶段' }],
  }
}

function handleDiscoverKeyword(state: ContractGameState, clauseId: string): ContractGameState {
  if (state.phase !== 'CONTRACT_DISCOVERY') return state
  if (state.discoveredClauseIds.includes(clauseId)) return state

  const clause = state.clausePool.find(c => c.id === clauseId)
  if (!clause) return state

  return {
    ...state,
    discoveredClauseIds: [...state.discoveredClauseIds, clauseId],
    lastDiscovery: { clauseId, clauseText: clause.text },
    lastDecoyFeedback: null,
    log: [...state.log, { action: `DISCOVER(${clauseId})`, result: `发现问题条款：${clause.shortText}` }],
  }
}

function handleClickDecoy(state: ContractGameState, decoyId: string): ContractGameState {
  if (state.phase !== 'CONTRACT_DISCOVERY') return state

  let feedback = '这是标准条款，没有问题。'
  for (const section of CONTRACT_DOCUMENT.sections) {
    for (const para of section.paragraphs) {
      for (const seg of para.segments) {
        if (seg.type === 'decoy' && seg.id === decoyId) {
          feedback = seg.feedback
        }
      }
    }
  }

  return {
    ...state,
    lastDecoyFeedback: { decoyId, feedback },
    lastDiscovery: null,
  }
}

function handleDismissDiscovery(state: ContractGameState): ContractGameState {
  return {
    ...state,
    lastDiscovery: null,
    lastDecoyFeedback: null,
  }
}

// =============================================================================
// Action Handlers — Pack Shop
// =============================================================================

function handleStartShop(state: ContractGameState): ContractGameState {
  // Allow entry from CONTRACT_DISCOVERY (initial shop)
  if (state.phase !== 'CONTRACT_DISCOVERY') return state

  return {
    ...state,
    phase: 'PACK_SHOP',
    isInterShop: false,
    lastDiscovery: null,
    lastDecoyFeedback: null,
    jokerPool: getShopJokerPool(state),
    shopMoney: POKER_CONFIG.SHOP_MONEY,
    log: [...state.log, { action: 'START_SHOP', result: '进入卡包商店' }],
  }
}

/** Calculate blind completion reward: base reward + remaining hands + interest */
export function calculateBlindReward(blindType: BlindType, handsRemaining: number, currentMoney: number) {
  const baseReward = BLIND_REWARDS[blindType] ?? 3
  const handsBonus = handsRemaining
  const interest = Math.min(Math.floor(currentMoney / 5), MAX_INTEREST)
  const total = baseReward + handsBonus + interest
  return { baseReward, handsBonus, interest, total }
}

/** Enter inter-shop (between blinds) */
function enterInterShop(state: ContractGameState): ContractGameState {
  const blindType = state.currentBlind?.type ?? 'small_blind'
  const reward = calculateBlindReward(blindType, state.handsRemaining, state.shopMoney)

  return {
    ...state,
    phase: 'PACK_SHOP',
    isInterShop: true,
    challengeSubPhase: null,
    handsRemaining: POKER_CONFIG.INITIAL_HANDS,
    discardsRemaining: POKER_CONFIG.INITIAL_DISCARDS,
    jokerPool: getShopJokerPool(state),
    shopMoney: state.shopMoney + reward.total,
    log: [...state.log, { action: 'ENTER_INTER_SHOP', result: `进入中间商店 奖励:${reward.baseReward}+手牌:${reward.handsBonus}+利息:${reward.interest}=$${reward.total}` }],
  }
}

function handleSelectFromPack(state: ContractGameState, packId: PackId, consumableId: string): ContractGameState {
  if (state.phase !== 'PACK_SHOP') return state
  if (state.isInterShop) return state  // No research packs in inter-shop
  if (state.purchasedPacks.length >= POKER_CONFIG.MAX_PACKS) return state
  if (state.purchasedPacks.includes(packId)) return state
  if (state.shopMoney < POKER_CONFIG.PACK_COST) return state

  const pack = ALL_PACKS.find(p => p.id === packId)
  const consumable = pack?.cards.find(c => c.id === consumableId)
  if (!pack || !consumable) return state

  // Get clue cards from the corresponding stakeholder
  const stakeholder = ALL_STAKEHOLDERS.find(s => s.id === pack.deckSource)
  const newClueCards = stakeholder?.cardPool ?? []

  // After purchasing, refresh Joker pool (buying packs may unlock new Jokers)
  const nextState: ContractGameState = {
    ...state,
    purchasedPacks: [...state.purchasedPacks, packId],
    consumables: [...state.consumables, consumable],
    collectedCards: [...state.collectedCards, ...newClueCards],
    shopMoney: state.shopMoney - POKER_CONFIG.PACK_COST,
    log: [...state.log, {
      action: `BUY_PACK(${packId})`,
      result: `购买${pack.name}，选择${consumable.name}，获得${newClueCards.length}张线索卡`,
    }],
  }

  return { ...nextState, jokerPool: getShopJokerPool(nextState) }
}

// =============================================================================
// Action Handlers — Joker Shop (single purchase + reroll)
// =============================================================================

/** Buy a single Joker from the shop */
function handleBuyJoker(state: ContractGameState, jokerId: string): ContractGameState {
  if (state.phase !== 'PACK_SHOP') return state
  if (state.equippedJokers.length >= state.maxJokers) return state

  const joker = state.jokerPool.find(j => j.id === jokerId)
  if (!joker) return state

  const price = JOKER_PRICE[jokerId] ?? 3
  if (state.shopMoney < price) return state

  const newEquipped = [...state.equippedJokers, joker]

  return {
    ...state,
    shopMoney: state.shopMoney - price,
    equippedJokers: newEquipped,
    jokerPool: state.jokerPool.filter(j => j.id !== jokerId),
    log: [...state.log, {
      action: `BUY_JOKER(${jokerId})`,
      result: `购买Joker: ${joker.name} ($${price})`,
    }],
  }
}

/** Reroll Joker shop — pay $2, get new random Jokers */
function handleRerollJokers(state: ContractGameState): ContractGameState {
  if (state.phase !== 'PACK_SHOP') return state
  if (state.shopMoney < POKER_CONFIG.REROLL_COST) return state

  return {
    ...state,
    shopMoney: state.shopMoney - POKER_CONFIG.REROLL_COST,
    jokerPool: getShopJokerPool(state),
    log: [...state.log, {
      action: 'REROLL_JOKERS',
      result: `重掷Joker商品 (-$${POKER_CONFIG.REROLL_COST})`,
    }],
  }
}

// =============================================================================
// Challenge handlers — Balatro poker system
// =============================================================================

function handleStartChallenge(state: ContractGameState): ContractGameState {
  if (state.phase !== 'PACK_SHOP') return state
  // Only from initial shop (not inter-shop — inter-shop uses CONTINUE_TO_NEXT_BLIND)
  if (state.isInterShop) return state

  return enterChallengeResolution({
    ...state,
    log: [...state.log, { action: 'START_CHALLENGE', result: '进入质证阶段 — 三局制 Blind 系统' }],
  })
}

/** Generate a random boss effect */
function pickBossEffect(): BossEffect {
  const idx = Math.floor(Math.random() * BOSS_EFFECTS.length)
  const template = BOSS_EFFECTS[idx]!
  const effect: BossEffect = { ...template } as BossEffect

  // For debuff_suit, pick a random suit
  if (effect.type === 'debuff_suit') {
    const suits = ALL_SUITS
    effect.debuffedSuit = suits[Math.floor(Math.random() * suits.length)]
  }

  return effect
}

/** Apply all clause pressures + build poker deck + generate blind configs */
function enterChallengeResolution(state: ContractGameState): ContractGameState {
  // 1. Apply all pressures at once
  let tracks = { ...state.tracks }
  for (const clause of state.clausePool) {
    tracks = applyDelta(tracks, clause.pressure)
  }

  // 2. Build poker deck from collected clue cards
  const deck = shuffleDeck(buildDeck(state.collectedCards))

  // 3. Jokers already equipped from pack shop
  const equipped = state.equippedJokers

  // 4. Consumables come from pack shop (already in state.consumables)
  const consumableHand = [...state.consumables]

  // 5. Generate blind configs
  const bossEffect = pickBossEffect()
  const blinds: BlindConfig[] = BLIND_CLAUSE_CONFIG.map(cfg => ({
    type: cfg.type,
    label: BLIND_LABELS[cfg.type].cn,
    clauseIds: cfg.clauseIds,
    bossEffect: cfg.type === 'boss' ? bossEffect : undefined,
  }))

  // 6. Build ALL clause thresholds (for tracking across all blinds)
  const clauseThresholds: ClauseThreshold[] = CLAUSE_THRESHOLDS.map(t => ({
    clauseId: t.clauseId,
    threshold: t.threshold,
    reversed: false,
  }))

  return {
    ...state,
    phase: 'CHALLENGE_RESOLUTION',
    tracks,
    challengeSubPhase: 'BLIND_SELECT',
    deck: deck.slice(state.handSize),
    hand: deck.slice(0, state.handSize),
    discardPile: [],
    selectedCardIds: [],
    playedCards: [],
    drawnCardIds: [],
    handsRemaining: POKER_CONFIG.INITIAL_HANDS,
    discardsRemaining: POKER_CONFIG.INITIAL_DISCARDS,
    runningScore: 0,
    lastHandResult: null,
    clauseThresholds,
    allClauseThresholds: clauseThresholds.map(t => ({ ...t })),
    reversedClauseIds: [],
    newlyReversedClauseId: null,
    jokerPool: [],
    equippedJokers: equipped,
    consumableHand,
    handTypeLevels: state.handTypeLevels,
    clauseOutcomes: [],
    contractTags: [],
    // Blind system
    currentBlindIndex: 0,
    blinds,
    currentBlind: blinds[0] ?? null,
    bossEffect: null,  // Only set when entering boss blind
    isInterShop: false,
    usedHandTypes: [],
    lockedHandType: null,
    log: [...state.log, {
      action: 'ENTER_CHALLENGE',
      result: `质证开始：${deck.length}张牌，${equipped.length}张Joker，${consumableHand.length}张消耗品，三局制`,
    }],
  }
}

/** START_BLIND: from BLIND_SELECT → CARD_SELECT, set up current blind */
function handleStartBlind(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'BLIND_SELECT') return state

  const blind = state.blinds[state.currentBlindIndex]
  if (!blind) return state

  // Start from global caps, then apply Joker effects
  let hands: number = POKER_CONFIG.INITIAL_HANDS
  let discards: number = POKER_CONFIG.INITIAL_DISCARDS
  for (const j of state.equippedJokers) {
    if (j.id === 'J13') discards += 1  // 不可抗力: +1 discard
    if (j.id === 'J14') hands += 1     // 缓兵之计: +1 hand
  }

  // Boss effects that modify resources
  const isBoss = blind.type === 'boss'
  const bossEffect = isBoss ? (blind.bossEffect ?? null) : null

  if (bossEffect?.type === 'no_discards') discards = 0
  const handSize = bossEffect?.type === 'minus_hand_size'
    ? state.handSize - 1
    : state.handSize

  // Build clause thresholds for this blind only
  const blindThresholds: ClauseThreshold[] = blind.clauseIds.map(cid => {
    const existing = state.clauseThresholds.find(t => t.clauseId === cid)
    return existing
      ? { ...existing, reversed: false }  // Reset reversed for this blind's tracking
      : { clauseId: cid, threshold: CLAUSE_THRESHOLDS.find(t => t.clauseId === cid)?.threshold ?? 0, reversed: false }
  })

  // Reshuffle: combine deck + hand + discardPile, then deal
  const allCards = shuffleDeck([...state.deck, ...state.hand, ...state.discardPile])
  const handCards = allCards.slice(0, handSize)
  const deckCards = allCards.slice(handSize)

  return {
    ...state,
    challengeSubPhase: 'CARD_SELECT',
    currentBlind: blind,
    bossEffect,
    handsRemaining: hands,
    discardsRemaining: discards,
    runningScore: 0,
    lastHandResult: null,
    selectedCardIds: [],
    playedCards: [],
    drawnCardIds: [],
    newlyReversedClauseId: null,
    // Use blind-specific thresholds for display, but keep master list
    clauseThresholds: blindThresholds,
    hand: handCards,
    deck: deckCards,
    discardPile: [],
    usedHandTypes: [],
    lockedHandType: null,
    log: [...state.log, {
      action: `START_BLIND(${blind.type})`,
      result: `${blind.label}开始：${blind.clauseIds.length}个条款，${hands}手${discards}弃`,
    }],
  }
}

/** CONTINUE_TO_SHOP: from BLIND_COMPLETE → inter shop */
function handleContinueToShop(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'BLIND_COMPLETE') return state

  return enterInterShop({
    ...state,
    currentBlindIndex: state.currentBlindIndex + 1,
  })
}

/** CONTINUE_TO_NEXT_BLIND: from inter shop → next blind intro */
function handleContinueToNextBlind(state: ContractGameState): ContractGameState {
  if (state.phase !== 'PACK_SHOP') return state
  if (!state.isInterShop) return state

  const nextBlind = state.blinds[state.currentBlindIndex]
  if (!nextBlind) return state

  return {
    ...state,
    phase: 'CHALLENGE_RESOLUTION',
    challengeSubPhase: 'BLIND_SELECT',
    currentBlind: nextBlind,
    handsRemaining: POKER_CONFIG.INITIAL_HANDS,
    discardsRemaining: POKER_CONFIG.INITIAL_DISCARDS,
    isInterShop: false,
    runningScore: 0,
    log: [...state.log, {
      action: 'CONTINUE_TO_NEXT_BLIND',
      result: `进入${nextBlind.label}`,
    }],
  }
}

/** Resolve which Jokers are unlocked based on game state */
function resolveJokerUnlocks(state: ContractGameState): Joker[] {
  const cards = state.collectedCards
  const discovered = state.discoveredClauseIds
  const packs = state.purchasedPacks

  // Count sources
  const sourceCounts: Record<string, number> = {}
  for (const c of cards) {
    sourceCounts[c.source] = (sourceCounts[c.source] ?? 0) + 1
  }

  // Pack combo checks
  const hasPack = (id: PackId) => packs.includes(id)
  const packCount = packs.length

  const unlocked: Joker[] = []
  for (const joker of ALL_JOKERS) {
    let shouldUnlock = false
    switch (joker.id) {
      // 基础池 — 进入商店即可用
      case 'J01': shouldUnlock = discovered.length >= 1; break                  // 发现1+条款
      case 'J06': shouldUnlock = discovered.length >= 1; break                  // 发现1+条款
      case 'J11': shouldUnlock = discovered.length >= 1; break                  // 发现1+条款
      case 'J09': shouldUnlock = discovered.length >= 2; break                  // 发现2+条款
      case 'J03': shouldUnlock = discovered.length >= 3; break                  // 发现3+条款
      // 买包解锁
      case 'J02': shouldUnlock = hasPack('expert_interview'); break             // 买专家包
      case 'J04': shouldUnlock = hasPack('competitor_analysis'); break          // 买竞对包
      case 'J05': shouldUnlock = hasPack('industry_report'); break              // 买行业包
      case 'J13': shouldUnlock = hasPack('expert_interview'); break             // 买专家包
      // 进阶解锁
      case 'J07': shouldUnlock = discovered.length >= 7; break                  // 发现全部7条款
      case 'J08': shouldUnlock = Object.values(sourceCounts).some(c => c >= 3); break
      case 'J10': shouldUnlock = packCount >= 2; break                          // 买2包
      case 'J12': shouldUnlock = false; break                                   // 不再有合成卡
      case 'J14': shouldUnlock = cards.length >= 8; break                       // 8+张卡
      case 'J15': shouldUnlock = cards.length >= 12; break                      // 12+张卡 (2包 = 12)
    }
    if (shouldUnlock) {
      unlocked.push({ ...joker, unlocked: true })
    }
  }
  return unlocked
}

/** Select a card from hand */
function handleSelectHandCard(state: ContractGameState, cardId: string): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'CARD_SELECT') return state

  if (!state.hand.some(c => c.id === cardId)) return state
  if (state.selectedCardIds.includes(cardId)) return state
  if (state.selectedCardIds.length >= state.maxPlayCards) return state

  return {
    ...state,
    selectedCardIds: [...state.selectedCardIds, cardId],
  }
}

/** Deselect a card from hand */
function handleDeselectHandCard(state: ContractGameState, cardId: string): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'CARD_SELECT') return state

  return {
    ...state,
    selectedCardIds: state.selectedCardIds.filter(id => id !== cardId),
  }
}

/** Play selected cards — evaluate hand type, calculate score (deferred to animation end) */
function handlePlayHand(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'CARD_SELECT') return state
  if (state.selectedCardIds.length === 0) return state
  if (state.handsRemaining <= 0) return state

  // Boss: must_play_5 — require exactly 5 cards
  if (state.bossEffect?.type === 'must_play_5' && state.selectedCardIds.length !== 5) return state

  const playedCards = state.selectedCardIds
    .map(id => state.hand.find(c => c.id === id))
    .filter((c): c is PlayingCard => c !== undefined)

  const heldCards = state.hand.filter(c => !state.selectedCardIds.includes(c.id))

  // Evaluate hand type
  const evaluation = evaluatePokerHand(playedCards)

  // Boss: no_repeat_hand — if hand type already used, score = 0
  let bossZeroScore = false
  if (state.bossEffect?.type === 'no_repeat_hand' && state.usedHandTypes.includes(evaluation.handType)) {
    bossZeroScore = true
  }
  // Boss: one_hand_type — if locked and different, score = 0
  if (state.bossEffect?.type === 'one_hand_type' && state.lockedHandType && state.lockedHandType !== evaluation.handType) {
    bossZeroScore = true
  }

  // Get the level for this hand type
  const level = state.handTypeLevels[evaluation.handType]

  // Calculate how many hands have been played so far (for J07)
  const handsPlayed = POKER_CONFIG.INITIAL_HANDS - state.handsRemaining

  // Score the hand with detailed steps for animation
  let result = scoreHandDetailed(
    evaluation,
    level,
    state.equippedJokers,
    heldCards,
    state.deck.length,
    state.reversedClauseIds.length,
    handsPlayed,
    state.bossEffect,
  )

  // If boss forces zero score, create a new result object (don't mutate)
  if (bossZeroScore) {
    result = {
      ...result,
      handScore: 0,
      totalChips: 0,
      totalMult: 0,
      scoringSteps: [{
        type: 'final_score',
        totalChips: 0,
        totalMult: 0,
        handScore: 0,
      }],
    }
  }

  // Remove played cards from hand → playedCards (NOT discardPile yet)
  const remainingHand = state.hand.filter(c => !state.selectedCardIds.includes(c.id))

  // Track used hand types for boss effects
  const newUsedHandTypes = [...state.usedHandTypes, evaluation.handType]
  const newLockedHandType = state.lockedHandType ?? (state.bossEffect?.type === 'one_hand_type' ? evaluation.handType : null)

  return {
    ...state,
    challengeSubPhase: 'SCORING_ANIMATION',
    hand: remainingHand,
    playedCards,
    selectedCardIds: [],
    handsRemaining: state.handsRemaining - 1,
    // runningScore NOT updated yet — deferred to FINISH_SCORING
    lastHandResult: result,
    usedHandTypes: newUsedHandTypes,
    lockedHandType: newLockedHandType,
    log: [...state.log, {
      action: `PLAY_HAND(${evaluation.handType})`,
      result: `${result.totalChips} × ${result.totalMult} = ${result.handScore} | 累计: ${state.runningScore} → ${state.runningScore + result.handScore}${bossZeroScore ? ' [Boss: 无效牌型]' : ''}`,
    }],
  }
}

/** Finish scoring animation — move playedCards to discard, update runningScore */
function handleFinishScoring(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'SCORING_ANIMATION') return state

  const handScore = state.lastHandResult?.handScore ?? 0
  const newScore = state.runningScore + handScore

  return advanceToMilestoneCheck({
    ...state,
    discardPile: [...state.discardPile, ...state.playedCards],
    playedCards: [],
    runningScore: newScore,
  })
}

/** Discard selected cards and draw replacements */
function handleDiscardSelected(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'CARD_SELECT') return state
  if (state.selectedCardIds.length === 0) return state
  if (state.discardsRemaining <= 0) return state

  const discardedCards = state.selectedCardIds
    .map(id => state.hand.find(c => c.id === id))
    .filter((c): c is PlayingCard => c !== undefined)

  const remainingHand = state.hand.filter(c => !state.selectedCardIds.includes(c.id))

  // Draw from deck to fill hand back to HAND_SIZE (or as many as available)
  const drawCount = Math.min(discardedCards.length, state.deck.length)
  const drawn = state.deck.slice(0, drawCount)
  const newDeck = state.deck.slice(drawCount)
  const newHand = [...remainingHand, ...drawn]

  return {
    ...state,
    hand: newHand,
    deck: newDeck,
    discardPile: [...state.discardPile, ...discardedCards],
    selectedCardIds: [],
    discardsRemaining: state.discardsRemaining - 1,
    drawnCardIds: drawn.map(c => c.id),
    log: [...state.log, {
      action: `DISCARD(${discardedCards.length})`,
      result: `弃${discardedCards.length}张，抽${drawn.length}张`,
    }],
  }
}

/** Use a consumable card (replaces tarot system) */
function handleUseConsumable(state: ContractGameState, consumableId: string, targetCardIds: string[]): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state
  if (state.challengeSubPhase !== 'CARD_SELECT') return state

  const idx = state.consumableHand.findIndex(c => c.id === consumableId)
  if (idx === -1) return state
  const consumable = state.consumableHand[idx]!

  let newHand = [...state.hand]
  let newDeck = [...state.deck]
  let newDiscardPile = [...state.discardPile]
  let newHandsRemaining = state.handsRemaining
  let newDiscardsRemaining = state.discardsRemaining

  switch (consumable.effectType) {
    case 'change_suit': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      const suits: PlayingCard['suit'][] = ['compute', 'data', 'network', 'infra']
      const currentIdx = suits.indexOf(target.suit)
      const nextSuit = suits[(currentIdx + 1) % suits.length]!
      newHand = newHand.map(c => c.id === target.id ? { ...c, suit: nextSuit } : c)
      break
    }
    case 'rank_up': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      const newRank = upgradeRank(target.rank)
      const chipValues: Record<number, number> = { 2: 2, 3: 3, 5: 5, 7: 7, 10: 10, 13: 10, 14: 11 }
      newHand = newHand.map(c => c.id === target.id
        ? { ...c, rank: newRank, chipValue: chipValues[newRank] ?? c.chipValue }
        : c
      )
      break
    }
    case 'copy_card': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      const copy: PlayingCard = { ...target, id: `${target.id}_copy` }
      newDeck = [...newDeck, copy]
      break
    }
    case 'destroy_for_hand': {
      const targets = targetCardIds.map(id => newHand.find(c => c.id === id)).filter(Boolean)
      if (targets.length < 2) return state
      newHand = newHand.filter(c => !targetCardIds.includes(c.id))
      newHandsRemaining += 1
      break
    }
    case 'foil': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      newHand = newHand.map(c => c.id === target.id
        ? { ...c, enhancement: 'foil' as const, bonusChips: c.bonusChips + 30 }
        : c
      )
      break
    }
    case 'holo': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      newHand = newHand.map(c => c.id === target.id
        ? { ...c, enhancement: 'holo' as const, bonusMult: c.bonusMult + 4 }
        : c
      )
      break
    }
    case 'wild': {
      const target = newHand.find(c => c.id === targetCardIds[0])
      if (!target) return state
      newHand = newHand.map(c => c.id === target.id ? { ...c, suit: 'wild' as const } : c)
      break
    }
    case 'retrieve': {
      const retrieve = newDiscardPile.slice(-3)
      newDiscardPile = newDiscardPile.slice(0, -3)
      newHand = [...newHand, ...retrieve]
      break
    }
    case 'plus_hand': {
      newHandsRemaining += 1
      break
    }
    case 'plus_discard': {
      newDiscardsRemaining += 1
      break
    }
    default:
      return state
  }

  // Remove used consumable
  const newConsumableHand = state.consumableHand.filter((_, i) => i !== idx)

  return {
    ...state,
    hand: newHand,
    deck: newDeck,
    discardPile: newDiscardPile,
    consumableHand: newConsumableHand,
    handsRemaining: newHandsRemaining,
    discardsRemaining: newDiscardsRemaining,
    log: [...state.log, {
      action: `USE_CONSUMABLE(${consumable.name})`,
      result: consumable.description,
    }],
  }
}

/** Advance through poker sub-phases:
 *  SCORING_ANIMATION → (via FINISH_SCORING) → CLAUSE_REVERSED or CARD_SELECT or FINAL_TALLY */
function handleAdvancePresentation(state: ContractGameState): ContractGameState {
  if (state.phase !== 'CHALLENGE_RESOLUTION') return state

  const subPhase = state.challengeSubPhase

  // CLAUSE_REVERSED → check for more reversals or draw+CARD_SELECT or FINAL_TALLY
  if (subPhase === 'CLAUSE_REVERSED') {
    // Apply the reversed clause effects
    const clauseId = state.newlyReversedClauseId
    if (!clauseId) return advanceToNextHand(state)

    const clause = state.clausePool.find(c => c.id === clauseId)
    if (!clause) return advanceToNextHand(state)

    const newTracks = applyDelta(state.tracks, clause.present_success.effect)
    const tagsAdded = clause.present_success.contract_tags_add
    const newContractTags = [...new Set([...state.contractTags, ...tagsAdded])]

    const outcome: ClauseOutcome = {
      clauseId,
      clauseText: clause.text,
      result: clause.isBoss ? 'full' : 'success',
      threshold: clause.threshold,
      tagsAdded,
      trackChanges: clause.present_success.effect,
    }

    // Check if there are more newly reversed clauses
    const hasSettlement = state.equippedJokers.some(j => j.id === 'J15')
    const allNewlyReversed = checkMilestones(
      state.runningScore,
      state.clauseThresholds,
      hasSettlement,
    )
    const alreadyProcessed = [...state.reversedClauseIds, clauseId]
    const nextReversed = allNewlyReversed.find(id => !alreadyProcessed.includes(id))

    const updatedThresholds = state.clauseThresholds.map(t =>
      t.clauseId === clauseId ? { ...t, reversed: true } : t
    )

    return {
      ...state,
      tracks: newTracks,
      contractTags: newContractTags,
      clauseOutcomes: [...state.clauseOutcomes, outcome],
      reversedClauseIds: [...state.reversedClauseIds, clauseId],
      clauseThresholds: updatedThresholds,
      allClauseThresholds: state.allClauseThresholds.map(t =>
        t.clauseId === clauseId ? { ...t, reversed: true } : t
      ),
      newlyReversedClauseId: nextReversed ?? null,
      challengeSubPhase: nextReversed ? 'CLAUSE_REVERSED' : 'MILESTONE_CHECK',
      log: [...state.log, {
        action: `CLAUSE_REVERSED(${clauseId})`,
        result: `条款${clauseId}反驳成功！`,
      }],
    }
  }

  // BLIND_COMPLETE → handled by CONTINUE_TO_SHOP or ADVANCE_PRESENTATION
  if (subPhase === 'BLIND_COMPLETE') {
    // For boss blind (last blind), advance to FINAL_TALLY
    if (state.currentBlindIndex >= state.blinds.length - 1) {
      return { ...state, challengeSubPhase: 'FINAL_TALLY' }
    }
    // For other blinds, this should be handled by CONTINUE_TO_SHOP action
    return state
  }

  // FINAL_TALLY → end game
  if (subPhase === 'FINAL_TALLY') {
    return endGame(state)
  }

  return state
}

/** Check milestones and auto-apply all reversals, then advance to next hand */
function advanceToMilestoneCheck(state: ContractGameState): ContractGameState {
  const hasSettlement = state.equippedJokers.some(j => j.id === 'J15')
  const newlyReversed = checkMilestones(
    state.runningScore,
    state.clauseThresholds,
    hasSettlement,
  )

  // Apply all newly reversed clauses immediately (no separate CLAUSE_REVERSED screen)
  let current = state
  for (const clauseId of newlyReversed) {
    if (current.reversedClauseIds.includes(clauseId)) continue

    const clause = current.clausePool.find(c => c.id === clauseId)
    if (!clause) continue

    const newTracks = applyDelta(current.tracks, clause.present_success.effect)
    const tagsAdded = clause.present_success.contract_tags_add
    const newContractTags = [...new Set([...current.contractTags, ...tagsAdded])]

    const outcome: ClauseOutcome = {
      clauseId,
      clauseText: clause.text,
      result: clause.isBoss ? 'full' : 'success',
      threshold: clause.threshold,
      tagsAdded,
      trackChanges: clause.present_success.effect,
    }

    current = {
      ...current,
      tracks: newTracks,
      contractTags: newContractTags,
      clauseOutcomes: [...current.clauseOutcomes, outcome],
      reversedClauseIds: [...current.reversedClauseIds, clauseId],
      clauseThresholds: current.clauseThresholds.map(t =>
        t.clauseId === clauseId ? { ...t, reversed: true } : t
      ),
      allClauseThresholds: current.allClauseThresholds.map(t =>
        t.clauseId === clauseId ? { ...t, reversed: true } : t
      ),
      log: [...current.log, {
        action: `CLAUSE_REVERSED(${clauseId})`,
        result: `条款${clauseId}反驳成功！`,
      }],
    }
  }

  return advanceToNextHand({ ...current, newlyReversedClauseId: null })
}

/** After milestone check, draw cards and go to CARD_SELECT or BLIND_COMPLETE or GAME_OVER */
function advanceToNextHand(state: ContractGameState): ContractGameState {
  const blindTarget = Math.max(...state.clauseThresholds.map(t => t.threshold))

  // Reached blind target — immediately clear blind regardless of remaining hands
  if (state.runningScore >= blindTarget) {
    return { ...state, challengeSubPhase: 'BLIND_COMPLETE', newlyReversedClauseId: null, drawnCardIds: [] }
  }

  if (state.handsRemaining <= 0) {
    // Failed to reach blind target — game over
    return endGame({
      ...state,
      newlyReversedClauseId: null,
      drawnCardIds: [],
      log: [...state.log, { action: 'BLIND_FAILED', result: `未达到目标分数 ${blindTarget}，游戏结束` }],
    })
  }

  // Draw cards to fill hand back to handSize (adjusted by boss effect)
  const handSize = state.bossEffect?.type === 'minus_hand_size'
    ? state.handSize - 1
    : state.handSize
  const deficit = handSize - state.hand.length
  const drawCount = Math.min(Math.max(0, deficit), state.deck.length)
  const drawn = state.deck.slice(0, drawCount)
  const newDeck = state.deck.slice(drawCount)

  return {
    ...state,
    hand: [...state.hand, ...drawn],
    deck: newDeck,
    challengeSubPhase: 'CARD_SELECT',
    selectedCardIds: [],
    newlyReversedClauseId: null,
    lastHandResult: null,
    drawnCardIds: drawn.map(c => c.id),
  }
}

function endGame(state: ContractGameState): ContractGameState {
  // Record all unreversed clauses (across all blinds) as failures
  const recordedIds = new Set(state.clauseOutcomes.map(o => o.clauseId))
  const failureOutcomes: ClauseOutcome[] = []

  // Collect all clause IDs from all blinds
  const allBlindClauseIds = new Set(state.blinds.flatMap(b => b.clauseIds))

  for (const clauseId of allBlindClauseIds) {
    if (recordedIds.has(clauseId)) continue
    const clause = state.clausePool.find(c => c.id === clauseId)
    if (!clause) continue
    failureOutcomes.push({
      clauseId: clause.id,
      clauseText: clause.text,
      result: 'failure',
      threshold: clause.threshold,
      tagsAdded: [],
      trackChanges: {},
    })
  }

  const allOutcomes = [...state.clauseOutcomes, ...failureOutcomes]
  const victory = isDeliverable(state.tracks)

  // Restore full clause thresholds for summary display
  const fullThresholds: ClauseThreshold[] = CLAUSE_THRESHOLDS.map(t => ({
    clauseId: t.clauseId,
    threshold: t.threshold,
    reversed: state.reversedClauseIds.includes(t.clauseId),
  }))

  return {
    ...state,
    phase: 'GAME_OVER',
    gameOver: true,
    victory,
    clauseOutcomes: allOutcomes,
    clauseThresholds: fullThresholds,
    allClauseThresholds: fullThresholds,
    challengeSubPhase: null,
    newlyReversedClauseId: null,
    log: [...state.log, {
      action: 'GAME_OVER',
      result: victory ? '胜利！合同在可交付区间' : '失败。合同超出可交付区间',
    }],
  }
}

// =============================================================================
// Debug: skip to next phase
// =============================================================================

function handleDebugSkipPhase(state: ContractGameState): ContractGameState {
  switch (state.phase) {
    case 'IDLE': {
      let s = handleStartGame()
      s = debugDiscoverAll(s)
      s = debugBuyAllPacks(s)
      return enterChallengeResolution({
        ...s,
        log: [...s.log, { action: 'DEBUG_SKIP', result: 'IDLE → CHALLENGE_RESOLUTION' }],
      })
    }
    case 'CONTRACT_DISCOVERY': {
      const s = debugDiscoverAll(state)
      return handleStartShop(s)
    }
    case 'PACK_SHOP': {
      if (state.isInterShop) {
        // From inter-shop, go to next blind
        return handleContinueToNextBlind(state)
      }
      const s = debugBuyAllPacks(state)
      return enterChallengeResolution({
        ...s,
        log: [...s.log, { action: 'DEBUG_SKIP', result: 'PACK_SHOP → CHALLENGE_RESOLUTION' }],
      })
    }
    case 'CHALLENGE_RESOLUTION': {
      return endGame({
        ...state,
        challengeSubPhase: 'FINAL_TALLY',
        log: [...state.log, { action: 'DEBUG_SKIP', result: 'CHALLENGE → GAME_OVER' }],
      })
    }
    default:
      return state
  }
}

/** Debug: advance one step within the current phase/sub-phase */
function handleDebugSkipStep(state: ContractGameState): ContractGameState {
  switch (state.phase) {
    case 'IDLE':
      return handleStartGame()

    case 'CONTRACT_DISCOVERY': {
      const s = debugDiscoverAll(state)
      return handleStartShop(s)
    }

    case 'PACK_SHOP': {
      if (state.isInterShop) {
        return handleContinueToNextBlind(state)
      }
      const s = debugBuyAllPacks(state)
      return enterChallengeResolution(s)
    }

    case 'CHALLENGE_RESOLUTION': {
      const sub = state.challengeSubPhase

      // BLIND_SELECT → start the blind
      if (sub === 'BLIND_SELECT') {
        return handleStartBlind(state)
      }

      // CARD_SELECT → auto-select cards and play hand
      if (sub === 'CARD_SELECT') {
        // Auto-select up to maxPlayCards from hand
        const selectCount = Math.min(state.maxPlayCards, state.hand.length)
        const autoSelected = state.hand.slice(0, selectCount).map(c => c.id)
        return handlePlayHand({
          ...state,
          selectedCardIds: autoSelected,
        })
      }

      // SCORING_ANIMATION → finish scoring
      if (sub === 'SCORING_ANIMATION') {
        return handleFinishScoring(state)
      }

      // MILESTONE_CHECK → advance presentation
      if (sub === 'MILESTONE_CHECK') {
        return handleAdvancePresentation(state)
      }

      // CLAUSE_REVERSED → advance presentation
      if (sub === 'CLAUSE_REVERSED') {
        return handleAdvancePresentation(state)
      }

      // BLIND_COMPLETE → continue to shop or final tally
      if (sub === 'BLIND_COMPLETE') {
        if (state.currentBlindIndex >= state.blinds.length - 1) {
          return { ...state, challengeSubPhase: 'FINAL_TALLY' }
        }
        return handleContinueToShop(state)
      }

      // FINAL_TALLY → end game
      if (sub === 'FINAL_TALLY') {
        return endGame(state)
      }

      return state
    }

    default:
      return state
  }
}

/** Discover all clause keywords */
function debugDiscoverAll(state: ContractGameState): ContractGameState {
  const allClauseIds = state.clausePool.map(c => c.id)
  return {
    ...state,
    discoveredClauseIds: allClauseIds,
    lastDiscovery: null,
    lastDecoyFeedback: null,
  }
}

/** Debug: buy first 2 packs + some Jokers */
function debugBuyAllPacks(state: ContractGameState): ContractGameState {
  let s = handleStartShop({ ...state, phase: 'CONTRACT_DISCOVERY' })

  for (const pack of ALL_PACKS.slice(0, POKER_CONFIG.MAX_PACKS)) {
    if (s.purchasedPacks.includes(pack.id)) continue
    s = handleSelectFromPack(s, pack.id, pack.cards[0]!.id)
  }
  for (const joker of s.jokerPool) {
    s = handleBuyJoker(s, joker.id)
  }
  return s
}

// =============================================================================
// Selectors / Derived state helpers
// =============================================================================

export function getContractOutput(state: ContractGameState) {
  return {
    tracks_final: { ...state.tracks },
    contract_tags: [...state.contractTags],
    success: state.victory,
    score: computeScore(state),
    clauseOutcomes: state.clauseOutcomes,
  }
}

/** Get hand cards not currently selected */
export function getUnselectedHandCards(state: ContractGameState): PlayingCard[] {
  const selectedSet = new Set(state.selectedCardIds)
  return state.hand.filter(c => !selectedSet.has(c.id))
}

/** Get selected hand cards in order */
export function getSelectedHandCards(state: ContractGameState): PlayingCard[] {
  return state.selectedCardIds
    .map(id => state.hand.find(c => c.id === id))
    .filter((c): c is PlayingCard => c !== undefined)
}

/** Get the next clause threshold to be broken */
export function getNextThreshold(state: ContractGameState): ClauseThreshold | null {
  return state.clauseThresholds.find(t => !t.reversed) ?? null
}

